import { formatDataLocal, nomeAeroporto } from '@agencia/shared';

export interface TrechoDto {
  id: string;
  companhia: string;
  numeroVoo: string;
  origem: string;
  destino: string;
  partida: string;
  chegada: string | null;
  partidaPrevista: string | null;
  statusVoo: string | null;
  consultadoEm: string | null;
  checkinFeitoEm: string | null;
  checkinPor: string | null;
  alteracao: string | null;
  alteracaoEm: string | null;
  alteracaoVistaEm: string | null;
  checkinUrl: string | null;
}

export interface VooProximo extends TrechoDto {
  reservaId: string;
  localizador: string;
  companhiaReserva: string;
  companhiaNome: string;
  sobrenome: string | null;
  passageiros: string[];
  trechoN: number;
  trechosTotal: number;
  cliente: { id: string; nome: string; telefone: string | null };
  consultor: { id: string; nome: string } | null;
  venda: { id: string; numero: number | null } | null;
  reservaConferida: { status: 'OK' | 'CANCELADA' | 'ALTERADA' | 'NAO_ENCONTRADA'; em: string } | null;
  /** Bilhete em PDF de onde a reserva foi lida (o que foi enviado ao cliente). */
  bilheteId: string | null;
}

export interface ProximosVoos {
  agora: string;
  alteracoesPendentes: number;
  monitorLigado: boolean;
  voos: VooProximo[];
}

export interface ReservaDto {
  id: string;
  localizador: string;
  companhia: string;
  companhiaNome: string;
  sobrenome: string | null;
  pedidoCia: string | null;
  passageiros: { nome: string; bilhete?: string }[];
  bagagem: string | null;
  origem: 'PDF_IA' | 'MANUAL';
  venda: { id: string; numero: number | null } | null;
  arquivo: { id: string; nome: string } | null;
  trechos: TrechoDto[];
}

export interface ReservasCliente {
  reservas: ReservaDto[];
  pdfs: { id: string; nome: string; voosLidoEm: string | null; voosErro: string | null }[];
  iaLigada: boolean;
  monitorLigado: boolean;
}

const STATUS: Record<string, string> = {
  Expected: 'Previsto',
  Unknown: 'Sem informação ainda',
  CheckIn: 'Check-in aberto no aeroporto',
  Boarding: 'Embarque',
  GateClosed: 'Portão fechado',
  Departed: 'Decolou',
  EnRoute: 'Em voo',
  Delayed: 'Atrasado',
  Approaching: 'Chegando',
  Arrived: 'Pousou',
  Canceled: 'Cancelado',
  Cancelled: 'Cancelado',
  CanceledUncertain: 'Talvez cancelado',
  Diverted: 'Desviado',
  NaoEncontrado: 'Não encontrado na companhia',
};

export function nomeStatus(s: string | null): string | null {
  return s ? (STATUS[s] ?? s) : null;
}

export const CORES_CIA: Record<string, string> = { G3: '#ff6a00', LA: '#1b0088', JJ: '#1b0088', AD: '#0f5fae' };

export function cancelado(t: Pick<TrechoDto, 'statusVoo'>): boolean {
  return /^cancel/i.test(t.statusVoo ?? '');
}

/** Mensagem pronta para o cliente (lembrete ou aviso de alteração). */
export function mensagemCliente(v: VooProximo): string {
  const nome = v.cliente.nome.trim().split(/\s+/)[0] ?? '';
  const voo = `${v.companhiaNome} ${v.companhia} ${v.numeroVoo}`;
  const quando = `${formatDataLocal(v.partida, true)} às ${v.partida.slice(11, 16)}`;
  const rota = `${nomeAeroporto(v.origem)} (${v.origem}) para ${nomeAeroporto(v.destino)} (${v.destino})`;
  if (v.alteracao && !v.alteracaoVistaEm) {
    if (cancelado(v)) return `Olá, ${nome}! A companhia informou o cancelamento do seu voo ${voo}, de ${quando}, de ${rota}. Já estamos cuidando da reacomodação e te passamos as opções em seguida.`;
    if (v.partidaPrevista) return `Olá, ${nome}! A companhia alterou o horário do seu voo ${voo} de ${rota}. A nova saída é ${formatDataLocal(v.partidaPrevista, true)} às ${v.partidaPrevista.slice(11, 16)}. Localizador: ${v.localizador}. Qualquer dúvida, estamos aqui!`;
    return `Olá, ${nome}! A companhia informou uma alteração no seu voo ${voo}, de ${quando}. Já estamos verificando e te avisamos em seguida. Localizador: ${v.localizador}.`;
  }
  const checkin = v.checkinFeitoEm ? 'Seu check-in já está feito ✅' : 'O check-in online pode ser feito pelo app ou site da companhia.';
  return `Olá, ${nome}! Passando para lembrar do seu voo ${voo}, ${quando}, de ${rota}. Localizador: ${v.localizador}. ${checkin} Chegue ao aeroporto com antecedência. Boa viagem! ✈️`;
}
